<template>
  <div class="card tf-card">
    <div class="card-body tf">

      <!-- строка 1: поиск + переключатель фильтров + действия страницы -->
      <div class="tf__row">
        <div class="tf__search">
          <b-input-group>
            <b-input-group-prepend is-text>
              <i class="fa fa-search"></i>
            </b-input-group-prepend>
            <b-form-input
                ref="search"
                v-model="searchInput"
                type="text"
                autocomplete="off"
                placeholder="Поиск по ID или названию турнира"
                @input="onSearchInput"
                @keydown.native.enter="applySearch"
                @keydown.native.esc="clearSearch"
            />
            <b-input-group-append v-if="searchInput">
              <b-button
                  variant="outline-secondary"
                  v-b-tooltip.hover.top="'Очистить (Esc)'"
                  @click="clearSearch"
              >
                <i class="fa fa-times"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <b-button
            variant="outline-primary"
            class="tf__toggle"
            @click="toggle"
        >
          <i class="fa fa-sliders"></i>
          Фильтры
          <b-badge v-if="activeCount" variant="primary" class="tf__toggle-badge">{{ activeCount }}</b-badge>
          <i class="fa tf__toggle-caret" :class="isOpen ? 'fa-angle-up' : 'fa-angle-down'"></i>
        </b-button>

        <div class="tf__count">
          <template v-if="loading">Загрузка…</template>
          <template v-else>Найдено: <b>{{ total }}</b></template>
        </div>

        <div class="tf__spacer"></div>

        <slot name="actions"/>
      </div>

      <!-- строка 2: сами фильтры -->
      <b-collapse v-model="isOpen">
        <div class="tf__fields">
          <div class="tf__field">
            <label>Дисциплина</label>
            <b-form-select v-model="gameModel" :options="gameOptions"/>
          </div>

          <div class="tf__field">
            <label>Тип турнира</label>
            <b-form-select v-model="variationModel" :options="variationOptions"/>
          </div>
        </div>
      </b-collapse>

      <!-- строка 3: что применено сейчас -->
      <div v-if="chips.length" class="tf__chips">
        <span class="tf__chips-label">Применено:</span>

        <button
            v-for="chip in chips"
            :key="chip.key"
            type="button"
            class="tf__chip"
            v-b-tooltip.hover.top="'Снять фильтр'"
            @click="removeChip(chip)"
        >
          <span class="tf__chip-name">{{ chip.name }}:</span>
          <span class="tf__chip-value">{{ chip.value }}</span>
          <i class="fa fa-times"></i>
        </button>

        <button type="button" class="tf__reset" @click="$emit('reset')">Сбросить всё</button>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from "lodash";

const OPEN_STATE_KEY = "tournamentsFiltersOpen";

export default {
  name: "TournamentsFilters",
  props: {
    // {search: String, game: Number|null, variation: Number|null}
    filters: {
      type: Object,
      required: true,
    },
    games: {
      type: Array,
      default: () => [],
    },
    variations: {
      type: Array,
      default: () => [],
    },
    total: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const savedState = localStorage.getItem(OPEN_STATE_KEY);
    return {
      searchInput: this.filters.search || "",
      isOpen: savedState === null ? true : savedState === "true",
    };
  },
  computed: {
    gameOptions() {
      return [{value: null, text: "Все дисциплины"}, ...(this.games || [])];
    },
    variationOptions() {
      return [{value: null, text: "Все типы турниров"}, ...(this.variations || [])];
    },

    gameModel: {
      get() {
        return this.filters.game;
      },
      set(value) {
        this.$emit("change", {game: value});
      },
    },
    variationModel: {
      get() {
        return this.filters.variation;
      },
      set(value) {
        this.$emit("change", {variation: value});
      },
    },

    activeCount() {
      return this.chips.length;
    },

    chips() {
      const chips = [];

      if (this.filters.search) {
        chips.push({key: "search", name: "Поиск", value: this.filters.search});
      }

      if (this.filters.game !== null && this.filters.game !== undefined) {
        chips.push({
          key: "game",
          name: "Дисциплина",
          value: this.optionText(this.games, this.filters.game),
        });
      }

      if (this.filters.variation !== null && this.filters.variation !== undefined) {
        chips.push({
          key: "variation",
          name: "Тип",
          value: this.optionText(this.variations, this.filters.variation),
        });
      }

      return chips;
    },
  },
  created() {
    this.debouncedSearch = lodash.debounce(this.applySearch, 500);
  },
  beforeDestroy() {
    this.debouncedSearch.cancel();
  },
  watch: {
    "filters.search"(value) {
      if (value !== this.searchInput) this.searchInput = value || "";
    },
  },
  methods: {
    optionText(options, value) {
      const option = (options || []).find((item) => item.value === value);
      return option ? option.text : value;
    },

    toggle() {
      this.isOpen = !this.isOpen;
      localStorage.setItem(OPEN_STATE_KEY, String(this.isOpen));
    },

    onSearchInput() {
      this.debouncedSearch();
    },

    applySearch() {
      this.debouncedSearch.cancel();
      this.$emit("change", {search: this.searchInput.trim()});
    },

    clearSearch() {
      this.searchInput = "";
      this.applySearch();
    },

    removeChip(chip) {
      if (chip.key === "search") {
        this.clearSearch();
        return;
      }
      this.$emit("change", {[chip.key]: null});
    },

    // используется родителем по горячей клавише «/»
    focusSearch() {
      const input = this.$refs.search;
      if (input && input.$el) input.$el.focus();
    },
  },
};
</script>

<style scoped lang="scss">
.tf-card {
  margin-bottom: 20px;
  overflow: visible;
}

.tf {
  padding: 16px 20px;

  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }

  &__spacer {
    flex: 1 1 auto;
  }

  &__search {
    flex: 1 1 300px;
    max-width: 400px;
    min-width: 220px;
  }

  &__toggle {
    white-space: nowrap;
  }

  &__toggle-badge {
    margin-left: 4px;
  }

  &__toggle-caret {
    margin-left: 6px;
  }

  &__count {
    font-size: 13px;
    color: #898989;
    white-space: nowrap;
  }

  /* ---- поля фильтров ---- */
  &__fields {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f1f1f1;
  }

  &__field {
    flex: 0 1 230px;
    min-width: 180px;

    label {
      display: block;
      margin-bottom: 4px;
      font-size: 12px;
      color: #898989;
    }
  }

  /* ---- применённые фильтры ---- */
  &__chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
  }

  &__chips-label {
    font-size: 12px;
    color: #898989;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 320px;
    padding: 4px 10px;
    border: 1px solid #ded9ff;
    border-radius: 14px;
    background: #f4f2ff;
    font-size: 12px;
    color: #4c4c4c;

    &:hover {
      border-color: #7366ff;
      background: #ece9ff;
    }

    i {
      font-size: 10px;
      color: #8f8f8f;
    }
  }

  &__chip-name {
    color: #898989;
  }

  &__chip-value {
    overflow: hidden;
    max-width: 200px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__reset {
    padding: 4px 6px;
    border: 0;
    background: none;
    font-size: 12px;
    color: #898989;
    text-decoration: underline;

    &:hover {
      color: #2c323f;
    }
  }
}

@media (max-width: 767px) {
  .tf__search {
    max-width: none;
    flex-basis: 100%;
  }

  .tf__field {
    flex: 1 1 100%;
  }
}
</style>
